import { ErrorHandler } from '@src/components/ErrorHandler';
import fields from '@src/routes/private/MemberProfile/components/Modals/ConfComm/includes/fields.json';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { isEmpty, omitBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { InternationalAddress } from './components/InternationalAddress';
import { UsAddress } from './components/UsAddress';

/**
 * Address
 *
 * Displays address information about a member's policy.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function Address(props) {
  const { form, isAddressValid, isLineValid, mode, setIsAddressValid, targetRef, voidFormDisabled } = props;

  const [activeAddressTab, setActiveAddressTab] = useState(0);

  const allValues = form.watch();
  const isInternational = form.watch('ui[isInternational]');

  /**
   * Set default US address values when the address is international.
   */
  useEffect(() => {
    if (mode === 'create') {
      if (isInternational) {
        form.setValue('ui[usAddress][streetAddress]', 'ATTN International PROTECT');
        form.setValue('ui[usAddress][suite]', 'PO Box 46701');
        form.setValue('ui[usAddress][city]', 'Greensboro');
        form.setValue('ui[usAddress][postalCode]', '27420');
        form.setValue('ui[usAddress][stateOrRegion]', 'NC');
      } else {
        form.setValue('ui[usAddress][streetAddress]', '');
        form.setValue('ui[usAddress][suite]', '');
        form.setValue('ui[usAddress][city]', '');
        form.setValue('ui[usAddress][postalCode]', '');
        form.setValue('ui[usAddress][stateOrRegion]', '');
      }
    }
  }, [isInternational]);

  /**
   * toggle the active tab based on type of address
   */
  useEffect(() => {
    if (isInternational) {
      setActiveAddressTab(1);
    } else {
      setActiveAddressTab(0);
    }
  }, [isInternational]);

  /**
   * Tabs
   *
   * @type {[{label: string, key: string, content: ((function(*): Element)|*), props: {}},{label: string, key: string,
   *   content: ((function(*): Element)|*), props: {className: (string), isDisabled: boolean}}]}
   */
  const tabs = useMemo(() => {
    return [
      {
        content: UsAddress,
        key: 'UsAddress',
        label: 'US Address',
        props: {},
      },
      {
        content: InternationalAddress,
        key: 'InternationalAddress',
        label: 'International Address',
        props: {
          className: !isInternational ? 'isDisabled' : '',
          isDisabled: !isInternational,
        },
      },
    ];
  }, [isInternational]);

  /**
   * Address Validation
   */
  useEffect(() => {
    const errors = {
      internationalAddress: {},
      usAddress: {},
    };

    if (allValues && allValues?.usAddress) {
      // US Address Validation
      Object.keys(allValues?.usAddress).forEach((key) => {
        if (fields?.usAddress?.[key]?.validators?.required === true) {
          const value = allValues?.usAddress[key];
          errors.usAddress[key] = isEmpty(value);
        }
      });
    }

    errors.usAddress = omitBy(errors.usAddress, (value) => {
      return value === false;
    });

    if (allValues && allValues?.isInternational) {
      // International Address Validation
      Object.keys(allValues?.internationalAddress).forEach((key) => {
        if (fields?.internationalAddress?.[key]?.validators?.required === true) {
          const value = allValues?.internationalAddress[key];
          errors.internationalAddress[key] = isEmpty(value);
        }
      });

      errors.internationalAddress = omitBy(errors.internationalAddress, (value) => {
        return value === false;
      });
    }

    const hasErrors = !isEmpty(errors?.usAddress) || !isEmpty(errors?.internationalAddress);

    if (hasErrors) {
      setIsAddressValid(false);
    } else {
      setIsAddressValid(true);
    }
  }, [allValues, activeAddressTab, isInternational]);

  return (
    <ErrorHandler location="src/routes/private/MemberProfile/components/Modals/Qmcso/Address/Address.jsx">
      <Tabs active={activeAddressTab} grow={false} onTabChange={setActiveAddressTab} size="sm">
        {tabs.map((tab) => {
          const TabComponent = tab?.content;

          return (
            <Tabs.Tab key={`address-tabs-${tab.key}`} label={tab.label} {...tab?.props}>
              <TabComponent
                activeAddressTab={activeAddressTab}
                form={form}
                isAddressValid={isAddressValid}
                isInternational={isInternational}
                isLineValid={isLineValid}
                setActiveAddressTab={setActiveAddressTab}
                targetRef={targetRef}
                voidFormDisabled={voidFormDisabled}
              />
            </Tabs.Tab>
          );
        })}
      </Tabs>
    </ErrorHandler>
  );
}

Address.propTypes = {
  defaultValues: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    countryCode: PropTypes.string,
    postalCode: PropTypes.string,
    stateOrRegion: PropTypes.string,
  }),
  form: PropTypes.shape({
    control: PropTypes.shape({
      getValues: PropTypes.func,
      setValue: PropTypes.func,
    }),
    getValues: PropTypes.func,
    setValue: PropTypes.func,
  }),
  isAddressValid: PropTypes.bool,
  mode: PropTypes.string,
  setIsAddressValid: PropTypes.func,
  targetRef: PropTypes.shape({
    current: PropTypes.instanceOf(PropTypes.element),
  }),
  voidFormDisabled: PropTypes.bool,
};

Address.defaultProps = {
  defaultValues: {},
  form: {},
  isAddressValid: false,
  mode: 'create',
  setIsAddressValid: () => {},
  targetRef: {},
  voidFormDisabled: false,
};
